/**
 * Sync Manager - socket reliability (cursor + catch-up)
 *
 * The server tags every `cache:invalidate` with a monotonic `seq` (the id of a
 * row in winery_event_log) and exposes GET /events?since=<seq>. We track the
 * last seq we applied per winery and, on (re)connect or window focus, ask the
 * server for everything we missed while the socket was down. This makes the
 * best-effort socket channel self-healing.
 *
 * Note on gap detection: `seq` is a GLOBAL serial, so per-winery values are
 * monotonic but NOT contiguous (other wineries consume ids in between).
 * Therefore we do NOT do "seq === lastSeq + 1" gap detection on live events
 * (it would false-positive constantly). Live events are applied as they arrive;
 * the real loss window — a dropped/closed socket — is healed by catchUp() on
 * reconnect and on focus.
 */

import { getTanks, getOperations, getLots } from './utils';
import { setTanks } from '../context/tanks/tanksSlice';
import { setOperations } from '../context/operations/operationsSlice';
import { setLots } from '../context/lots/lotsSlice';

const lastSeqByWinery = new Map();

export const getLastSeq = (wineryId) => lastSeqByWinery.get(wineryId) ?? 0;

const bumpSeq = (wineryId, seq) => {
  if (wineryId == null || seq == null || Number.isNaN(Number(seq))) return;
  const current = lastSeqByWinery.get(wineryId) ?? 0;
  if (Number(seq) > current) lastSeqByWinery.set(wineryId, Number(seq));
};

/**
 * Apply a single live `cache:invalidate` event and advance the cursor.
 * @param {Object} event - { affected_entities, seq, ... }
 */
export const applyEvent = async (event, { cacheManager, wineryId }) => {
  if (event?.affected_entities) {
    await cacheManager.processAffectedEntities(event.affected_entities);
  }
  bumpSeq(wineryId, event?.seq);
};

/**
 * Full refetch of the cellar core. Used when the server signals full_resync
 * (the client's cursor is older than the retained event window).
 */
const fullResync = async ({ dispatch, axios, wineryId }) => {
  console.log('🔁 Full resync: refetching tanks/operations/lots');
  const [tanks, operations, lots] = await Promise.all([
    getTanks(wineryId, axios),
    getOperations(axios),
    getLots(wineryId, axios),
  ]);
  if (Array.isArray(tanks)) dispatch(setTanks(tanks));
  if (Array.isArray(operations)) dispatch(setOperations(operations));
  if (Array.isArray(lots)) dispatch(setLots(lots));
};

/**
 * Catch up after a (re)connect or window focus: replay the events we missed,
 * or full-resync if the server says our cursor is too old. Safe to call often.
 */
export const catchUp = async ({ dispatch, axios, wineryId, cacheManager }) => {
  if (!wineryId) return;
  const since = getLastSeq(wineryId);

  try {
    const { data } = await axios.get('/events', { params: { since } });

    if (data?.full_resync) {
      await fullResync({ dispatch, axios, wineryId });
    } else if (Array.isArray(data?.events) && data.events.length > 0) {
      console.log(`🔄 Catch-up: replaying ${data.events.length} missed event(s)`);
      // Replay in order so deletes/updates apply as they originally happened.
      for (const ev of data.events) {
        if (ev?.affected_entities) {
          await cacheManager.processAffectedEntities(ev.affected_entities);
        }
      }
    }

    if (data?.latest_seq != null) bumpSeq(wineryId, data.latest_seq);
  } catch (err) {
    console.error('Catch-up failed:', err);
  }
};

/**
 * Optimistic-concurrency: build the `client_versions` payload for completing an
 * operation. Only tanks are versioned — tanks.updated_at is the clean
 * "cellar state changed" signal (bumped on real content changes via the server
 * contains trigger, NOT on planning). The values are server-sourced (from the
 * Redux tanks cache), so the server compares timestamp-vs-timestamp (no skew).
 *
 * @param {Object} operation - the operation being completed (has src_tanks/dest_tanks)
 * @param {Array} tanks - current Redux tanks cache (each with id + updated_at)
 * @returns {{tanks: Object}|undefined}
 */
export const buildOperationVersions = (operation, tanks = []) => {
  if (!operation) return undefined;

  const updatedAtById = new Map(tanks.map((t) => [t.id, t.updated_at]));

  const tankIds = new Set();
  for (const t of [...(operation.src_tanks || []), ...(operation.dest_tanks || [])]) {
    const id = t?.tank_id ?? t?.id;
    if (id != null) tankIds.add(id);
  }

  const tanksVersions = {};
  tankIds.forEach((id) => {
    const ts = updatedAtById.get(id);
    if (ts) tanksVersions[id] = ts;
  });

  return Object.keys(tanksVersions).length > 0 ? { tanks: tanksVersions } : undefined;
};

/**
 * Map a 409 `details.conflicts` list ([{type, id}]) into the affected_entities
 * shape the CacheManager consumes, so we can refetch exactly the stale entities.
 */
export const conflictsToAffectedEntities = (conflicts = []) => {
  const typeToKey = { tank: 'tanks', batch: 'batches', drug_batch: 'drug_batches' };
  const out = {};
  for (const c of conflicts) {
    const key = typeToKey[c?.type];
    if (!key || c?.id == null) continue;
    (out[key] = out[key] || []).push({ id: c.id, action: 'update' });
  }
  return out;
};
